import { browser } from '$app/environment';
import { translateInLang } from '$lib/i18n.svelte.js';
import { getCurrentPageLanguage } from '$lib/utils.svelte.js';

/**
 * @typedef {Object} ConsentState
 * @property {boolean} essential - Always true for essential cookies
 * @property {boolean} analytics - User's consent for analytics cookies
 * @property {boolean} hasBeenSet - Whether user has made a choice
 * @property {string} version - Consent version for future migrations
 * @property {Date} timestamp - When consent was given
 * @property {Date|null} lastRejected - When user last rejected cookies
 * @property {boolean} canReengage - Whether we can show banner again after rejection
 */

// Storage key for consent preferences
const CONSENT_STORAGE_KEY = 'ordalie_cookie_consent';
const CONSENT_VERSION = '1.3';
const CONSENT_EXPIRY_DAYS = 180; // 6 months as per CNIL requirements

// Banner configuration 
const BANNER_CONFIG = {
	position: 'bottom-left',
	timing: 2500, // Appear after 2.5s after engagement
};

/**
 * Get localized cookie category descriptions for current page language
 * @returns {{essential: string, analytics: string}} Cookie categories with localized descriptions
 */
export function getCookieCategories() {
	const currentLang = getCurrentPageLanguage();
	return {
		essential: translateInLang('cookies.essentialCookiesDescription', currentLang),
		analytics: translateInLang('cookies.analyticsCookiesDescription', currentLang)
	};
}

/**
 * Load consent state from localStorage
 * @returns {ConsentState}
 */
function loadConsentFromStorage() {
	if (!browser) {
		return {
			essential: true,
			analytics: false,
			hasBeenSet: false,
			version: CONSENT_VERSION,
			timestamp: new Date(),
			lastRejected: null,
			canReengage: true
		};
	}

	try {
		const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
		if (!stored) {
			return {
				essential: true,
				analytics: false,
				hasBeenSet: false,
				version: CONSENT_VERSION,
				timestamp: new Date(),
				lastRejected: null,
				canReengage: true
			};
		}

		const parsed = JSON.parse(stored);
		
		// Check version for future migrations
		if (parsed.version !== CONSENT_VERSION) {
			// Migrate from old version
			return {
				essential: true,
				analytics: Boolean(parsed.analytics),
				hasBeenSet: Boolean(parsed.hasBeenSet),
				version: CONSENT_VERSION,
				timestamp: new Date(parsed.timestamp || Date.now()),
				lastRejected: parsed.lastRejected ? new Date(parsed.lastRejected) : null,
				canReengage: true
			};
		}

		// Check if consent has expired (6 months)
		const consentAge = Date.now() - new Date(parsed.timestamp).getTime();
		const sixMonthsInMs = CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
		if (parsed.hasBeenSet && consentAge > sixMonthsInMs) {
			// Consent expired, reset to default state
			return {
				essential: true,
				analytics: false,
				hasBeenSet: false,
				version: CONSENT_VERSION,
				timestamp: new Date(),
				lastRejected: null,
				canReengage: true
			};
		}

		return {
			essential: true, // Always true
			analytics: Boolean(parsed.analytics),
			hasBeenSet: Boolean(parsed.hasBeenSet),
			version: parsed.version,
			timestamp: new Date(parsed.timestamp),
			lastRejected: parsed.lastRejected ? new Date(parsed.lastRejected) : null,
			canReengage: true
		};
	} catch (error) {
		console.error('Error loading cookie consent:', error);
		return {
			essential: true,
			analytics: false,
			hasBeenSet: false,
			version: CONSENT_VERSION,
			timestamp: new Date(),
			lastRejected: null,
			canReengage: true
		};
	}
}

/**
 * Save consent state to localStorage
 * @param {ConsentState} state
 */
function saveConsentToStorage(state) {
	if (!browser) return;

	try {
		localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
	} catch (error) {
		console.error('Error saving cookie consent:', error);
	}
}

// Core reactive state using Svelte 5 runes
let consentState = $state(loadConsentFromStorage());
let userEngaged = $state(false);
let manuallyTriggered = $state(false);

// Derived reactive values
const hasAnalyticsConsent = $derived(consentState.analytics);
const needsBanner = $derived(
	manuallyTriggered || (!consentState.hasBeenSet && userEngaged)
);
const isEssentialOnly = $derived(consentState.hasBeenSet && !consentState.analytics);

/**
 * Update consent preferences
 * @param {Partial<Pick<ConsentState, 'analytics'>>} updates
 */
function updateConsent(updates) {
	const newState = {
		...consentState,
		...updates,
		essential: true, // Always true
		hasBeenSet: true,
		version: CONSENT_VERSION,
		timestamp: new Date(),
		lastRejected: updates.analytics === false ? new Date() : consentState.lastRejected,
		canReengage: true // Always true now that we removed re-engagement logic
	};

	consentState = newState;
	saveConsentToStorage(newState);
	
	// Track consent metrics
	if (browser && typeof window !== 'undefined' && 'posthog' in window && window.posthog) {
		/** @type {any} */ (window.posthog).capture('cookie_consent_updated', {
			analytics_consent: updates.analytics,
			time_to_decision: Date.now() - userEngagementTime
		});
	}
}

/**
 * Accept all cookies
 */
function acceptAll() {
	updateConsent({ analytics: true });
}

/**
 * Reject all optional cookies (keep only essential)
 */
function rejectAll() {
	updateConsent({ analytics: false });
}

/**
 * Check if user has given consent for a specific category
 * @param {'essential' | 'analytics'} category
 * @returns {boolean}
 */
function hasConsent(category) {
	if (category === 'essential') return true;
	return consentState[category] || false;
}

// Track when user engagement started
let userEngagementTime = Date.now();

/**
 * Mark user as engaged (for delayed banner display)
 */
function markUserEngaged() {
	if (!browser) return;
	
	// Don't persist engagement - it should be fresh each page load
	userEngaged = true;
	userEngagementTime = Date.now();
}

/**
 * Check if user has engaged with the site
 * @returns {boolean}
 */
function hasUserEngaged() {
	return userEngaged;
}

/**
 * Manually trigger the banner to show (for preferences icon)
 */
function showBanner() {
	manuallyTriggered = true;
}

/**
 * Hide the manually triggered banner
 */
function hideBanner() {
	manuallyTriggered = false;
}

/**
 * Get banner configuration
 * @returns {{position: string, timing: number}}
 */
function getBannerConfig() {
	return BANNER_CONFIG;
}

/**
 * Reset consent state (for testing/debugging)
 */
function resetConsent() {
	if (!browser) return;
	
	localStorage.removeItem(CONSENT_STORAGE_KEY);
	consentState = {
		essential: true,
		analytics: false,
		hasBeenSet: false,
		version: CONSENT_VERSION,
		timestamp: new Date(),
		lastRejected: null,
		canReengage: true
	};
	userEngaged = false;
}

// Export reactive cookie consent API
export const cookieConsent = {
	// Reactive state (read-only)
	get state() { return consentState; },
	get hasAnalyticsConsent() { return hasAnalyticsConsent; },
	get needsBanner() { return needsBanner; },
	get isEssentialOnly() { return isEssentialOnly; },
	
	// Actions
	updateConsent,
	acceptAll,
	rejectAll,
	hasConsent,
	resetConsent,
	markUserEngaged,
	hasUserEngaged,
	getBannerConfig,
	showBanner,
	hideBanner,
	manuallyTriggered
};